import { WidgetRendererBase } from "../widget-renderer-base";
import {
    IPricingTableWidgetData,
    IPricingTableWidgetShortData,
    IPricingPlanShort,
    PricingTableWidgetData
} from "@pageBuilder/typings";
import { PricingTableWidgetScriptRenderer } from "./pricing-table-widget-script-renderer";
import { Base64Util } from "../../../utils/base64-util";
import { PricingTableWidgetStyleRenderer } from "./pricing-table-widget-style-renderer";
import { IRenderingCommonContext } from "@commonRendering/types";

export class PricingTableWidgetRenderer extends WidgetRendererBase<IPricingTableWidgetData> {

    private readonly DATA_ATTRIBUTE = "data-pricing-plan";
    private readonly scriptRenderer: PricingTableWidgetScriptRenderer;
    private readonly pricingPlanStyleRenderer: PricingTableWidgetStyleRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.scriptRenderer = new PricingTableWidgetScriptRenderer();
        this.pricingPlanStyleRenderer = new PricingTableWidgetStyleRenderer(this.styleService);
    }

    protected override renderInternal(element: HTMLElement, data: IPricingTableWidgetData): HTMLElement {
        const content = element.querySelector(".widget-content") as HTMLElement;
        this.removePlaceholder(content);

        if (!data.pricingPlans?.length) {
            this.renderPlaceholder(content);
        }

        this.setAttributes(element, data);
        this.renderStyles(element, data);
        this.renderScripts(element, data);

        return element;
    }

    protected override createNewInternal(): HTMLElement {
        const element = this.createWidgetElement("pricingTableWidget");
        const content = element.querySelector(".widget-content") as HTMLElement;
        this.renderPlaceholder(content);
        return element;
    }

    private renderScripts(element: HTMLElement, data: IPricingTableWidgetData): void {
        this.scriptRenderer.renderCommon(this.renderedDocument);

        const previousData = this.getData(element);
        if (PricingTableWidgetData.isLocalScriptRerenderNeeded(previousData, data)) {
            this.scriptRenderer.renderLocal(element);
        }
    }

    private renderPlaceholder(content: HTMLElement): void {
        const placeholder = document.createElement("div");
        placeholder.classList.add("not-available");
        content.appendChild(placeholder);
        return;
    }

    private removePlaceholder(element: HTMLElement): void {
        const notAvailableElement = element.querySelector(".not-available");
        notAvailableElement?.remove();
    }

    private setAttributes(element: HTMLElement, data: IPricingTableWidgetData): void {
        if (this.isEditorRendering) {
            element.setAttribute("data-draft", "true");
        }

        const shortData = this.getShortData(data);
        const json = Base64Util.serialize(shortData) || "";

        element.setAttribute(this.DATA_ATTRIBUTE, json);
    }

    private getShortData(data: IPricingTableWidgetData): IPricingTableWidgetShortData {
        return {
            pricingPlans: data.pricingPlans.map(pricingPlan => ({
                productId: pricingPlan.productId,
                theme: pricingPlan.theme
            })) as IPricingPlanShort[],
            style: {
                spaceBetweenItems: data.style.spaceBetweenItems,
                showDiscount: data.style.showDiscount,
                defaultThemeStyle: {
                    buttonTheme: data.style.defaultThemeStyle.buttonStyle?.theme,
                    imageRatio: data.style.defaultThemeStyle.imageRatio,
                    mainFeaturesStyle: data.style.defaultThemeStyle.mainFeaturesStyle,
                    enabledComponents: data.style.defaultThemeStyle.enabledComponents
                },
                highlightedThemeStyle: {
                    buttonTheme: data.style.highlightedThemeStyle.buttonStyle?.theme,
                    imageRatio: data.style.highlightedThemeStyle.imageRatio,
                    mainFeaturesStyle: data.style.highlightedThemeStyle.mainFeaturesStyle,
                    enabledComponents: data.style.highlightedThemeStyle.enabledComponents,
                    ribbonStyle: data.style.highlightedThemeStyle.ribbonStyle
                }
            },
            switcherStyle: {
                type: data.style.switcherStyle.type
            },
            highlightedPlanEnabled: data.highlightedPlanEnabled
        };
    }

    protected override renderStyles(element: HTMLElement, data: IPricingTableWidgetData): void {
        this.pricingPlanStyleRenderer.render(element, data);
        this.animationRenderer.setAnimationStyles(element, data.style.animation);
    }

    protected override getContentSelectorsInternal(element: HTMLElement): string[] {
        return this.pricingPlanStyleRenderer.getContentSelectors(element);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected override cleanupInternal(element: HTMLElement): void {
        // do nothing
    }

}
